import React, { useMemo } from 'react';
import { BaseComponentProps } from '@/pages/benchmark/types/commom';
import CardBox from '@/pages/dashboard/components/CardBox';
import { DATA_TYPE_OBJECT, getName, selectValByCondition } from '@home/utils/common';
import classNames from 'classnames';
import { handleDownload, jumpDetail } from '@/pages/dashboard/utils/utils';
import { getEchartTooltipString } from '@/pages/dashboard/components/EchartsTooltip';
import MetricCardTitle from '@/pages/dashboard/components/MetricCardItem/MetricCardTitle';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import PieChart from '@/pages/dashboard/components/Chart/components/PieChart';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import CommonBubble from '@home/components/CommonBubble';
import useStudioExplain from '@home/hooks/useStudioExplain';
import { mapObjectValue } from '@home/pages/studio/utils';
import './index.scss';
import { JumpType, navigate } from '@home/utils/utils';

export type TComponentMetric = {
  metricKey: string;
  nameEn: string;
  nameCn: string;
}

export interface IProfitDistributionCardProps extends BaseComponentProps {
  dataFormatRule: string;
  iconType: string;
  titleEn: string;
  titleCn: string;
  explainEn?: string;
  explainCn?: string;
  jumpParams?: any;
  jumpUrl?: string;
  jumpType?: JumpType;
  gameCode?: string;
  componentMetrics: TComponentMetric[];
  rainbowKey?: string;
  tableColumns: {
    metricKey: string;
    nameEn: string;
    nameCn: string;
    widthEn: number;
    widthCn: number;
    align: string;
    formatRule: string;
  }[];
  [key: string]: any;
}

export type TPieDataItem = {
  key: string;
  value: number;
  share: number;
};

const ProfitDistributionCard = (props: IProfitDistributionCardProps) => {
  const {
    style,
    className,
    dataKey = 'data',
    data,
    iconType,
    titleEn,
    titleCn,
    explainEn,
    explainCn,
    componentMetrics = [],
    dataFormatRule,
    tableColumns = [],
    jumpUrl,
    jumpType = JumpType.Window,
    jumpParams = {},
    filter,
    rainbowKey = '',
  } = props || {};

  const { errorMsg, loading, data: pieDataList = [] } = data[dataKey] || {};

  const { locale } = useIntl();

  const isEN = locale === LANGUAGE_EN;

  // 获取指标注释
  const { tipText } = useStudioExplain(rainbowKey, explainEn, explainCn);

  const title = getName(isEN, titleEn, titleCn);

  const { isSingle, jumpTime } = filter || {};

  // 组装饼图参数
  const { dataList, pieLegendData } = useMemo(() => {
    // 图例
    const pieLegendData: string[] = [];
    // 饼图数据
    const dataList: {
      value: number;
      share: number;
      name: string;
      key: string;
    }[] = [];

    componentMetrics?.forEach((item) => {
      const { metricKey, nameCn, nameEn } = item;
      const name = getName(isEN, nameEn, nameCn);
      pieLegendData.push(name);

      // 获取指标原始数据
      const currentData =
        pieDataList?.find(
          (item: TPieDataItem) =>
            item?.key?.toLowerCase() === metricKey?.toLowerCase(),
        ) || {};

      dataList.push({
        key: metricKey,
        value: currentData.value,
        share: currentData.share,
        name,
      });
    });

    return {
      dataList,
      pieLegendData,
    };
  }, [componentMetrics, isEN, pieDataList]);

  const newTableColumns = tableColumns?.map((item) => {
    const {
      nameCn,
      nameEn,
      widthEn,
      widthCn,
      align,
      metricKey,
      formatRule,
    } = item;
    const header = getName(isEN, nameEn, nameCn);
    const width = selectValByCondition(isEN, widthEn, widthCn);

    return {
      key: metricKey,
      header,
      align,
      width,
      formatRule,
      render: (record: any) =>
        dataFormat({ value: record[metricKey], formatRule }),
    };
  });

  // 浮窗
  const getTooltipText = (params: any) => {
    const { dataIndex, name, color, value } = params;

    const { start_time, last_date } = filter || {};

    // 获取指标原始数据
    const currentKey = componentMetrics?.[dataIndex]?.metricKey;
    const currentOrgData =
      pieDataList?.find((item: TPieDataItem) => item.key === currentKey) || {};

    return getEchartTooltipString({
      table: {
        data: [{ name, value, color, share: currentOrgData.share }],
        columns: [
          {
            key: 'name',
            header: `${start_time} ~ ${last_date}`,
            showIcon: true,
          },
          {
            key: 'value',
            header: '',
            dataFormatRule,
            align: 'right',
          },
          {
            key: 'share',
            header: '',
            dataFormatRule: 'percent1Decimal',
            align: 'right',
          },
        ],
      },
    });
  };

  // 格式化饼图label
  const labelFormat = (opts: any) => {
    const newOpts = { ...opts };
    newOpts.series = newOpts.series?.map((item: any) => {
      item.label.formatter = (params: any) => {
        const { name, value, data } = params;
        const newValue = dataFormat({
          value,
          formatRule: dataFormatRule,
        });
        const newPercent = dataFormat({
          value: data.share,
          formatRule: 'percent1Decimal',
        });
        return `{name|${name}}：{per|${newValue}(${newPercent})}\n{hr|}\n{a|}`;
      };
      item.labelLine.normal = { length: 5 };
      item.clockwise = false;
      return {
        ...item,
      };
    });
    return newOpts;
  };

  const renderList = useMemo(
    () => [
      {
        type: 'pieChart',
        svgType: 'pie-chart-icon',
        component: (
          <PieChart
            className='dbh-profit-distribution-pie-containre'
            height={145}
            isNodata={pieDataList?.length === 0}
            isLoading={loading}
            chartData={dataList}
            legendData={pieLegendData}
            formatterFunc={getTooltipText}
            setOtherConfigFunc={labelFormat}
          />
        ),
      },
      {
        type: 'sheet',
        svgType: 'sheet',
        component: (
          <MiniTable
            height={145}
            loading={loading}
            tableOption={newTableColumns}
            tableList={dataList}
          />
        ),
      },
    ],
    [dataList, loading, newTableColumns, pieLegendData],
  );

  // 筛选器继承参数
  const filterCarry = useMemo(() => {
    const {
      date_type: dataType,
      start_time: startTime,
      end_time: endTime,
      payType,
    } = filter || {};

    return {
      start: startTime,
      end: endTime,
      timeType: DATA_TYPE_OBJECT[dataType],
      ...(payType ? { payment_type: payType } : {}),
    };
  }, [filter]);

  // 卡片下钻
  const onClick = () => {
    if (jumpUrl) {
      // 筛选器继承跳转
      if (jumpParams?.filterValue && isSingle && jumpParams?.isComplete && jumpTime) {
        jumpParams.filterValue.rangeTime = jumpTime;
      }
      if (jumpParams?.filterValue) {
        jumpParams.filterValue = mapObjectValue(jumpParams.filterValue, filter);
      }

      // 下钻逻辑
      // jumpDetail(jumpUrl, jumpParams);

      navigate(jumpUrl, {
        jumpType,
        windowParams: filterCarry,
      });
    }
  };

  return (
    <CardBox
      className={classNames(
        className,
        'dbh-profit-distribution-container dbd-overlay-card overlay-card',
      )}
      style={style}
      titleCom={
        <CommonBubble content={tipText}>
          <div onClick={onClick}>
            <MetricCardTitle
              dataSource={{
                name: title,
                icon: iconType,
              }}
              jumpUrl={jumpUrl}
            />
          </div>
        </CommonBubble>
      }
      key={'ProfitDistributionCard'}
      renderList={renderList}
      downloadClick={async () => {
        // 格式化数据格式
        const newDataList = dataList?.map((item) => {
          const newData = { ...item };
          newTableColumns?.forEach((col) => {
            const { key, formatRule } = col || {};
            const value = newData[key];
            newData[key] = formatRule
              ? dataFormat({ value, formatRule })
              : value;
          });
          return newData;
        });
        handleDownload({ option: newTableColumns, data: newDataList }, title);
      }}
      isLoading={loading}
      isError={!!errorMsg}
      isNodata={pieDataList?.length === 0}
    />
  );
};
export default ProfitDistributionCard;
